import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { getSignContract, SignContractPayload, submitSignContract } from 'services/contractSign';
import { generateContractPdf } from 'utils/generateContractPdf';

const PageSignContract: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [contract, setContract] = useState<SignContractPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError('Invalid sign link.');
        setLoading(false);
        return;
      }
      try {
        const data = await getSignContract(token);
        setContract(data);
        setSignerName(data.proposal?.contact_name || '');
        setPurchaseOrderNumber(data.purchase_order_number || '');
        setVatNumber(data.vat_number || '');
        if (data.status === 'signed') {
          setSignerName(data.signer_name || '');
        }
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Unable to load contract.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !signerName.trim() || !agreed) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await submitSignContract(token, {
        signer_name: signerName.trim(),
        agreed: true,
        purchase_order_number: purchaseOrderNumber.trim() || undefined,
        vat_number: vatNumber.trim() || undefined,
      });
      setContract(updated);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to sign contract.');
    } finally {
      setSubmitting(false);
    }
  };

  const isSigned = contract?.status === 'signed';

  const handleDownloadPdf = async () => {
    if (!contract || generatingPdf) return;
    setGeneratingPdf(true);
    try {
      await generateContractPdf(contract);
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className="nc-PageSignContract">
      <Helmet>
        <title>Sign contract || Torch Events</title>
      </Helmet>
      <div className="container relative pt-8 pb-20 lg:pt-10">
        <header className="mx-auto mb-8 max-w-3xl text-center">
          {contract?.company_logo && (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/${contract.company_logo}`}
              alt={`${contract.company_name || 'Company'} logo`}
              className="mx-auto mb-5 max-h-24 max-w-[240px] object-contain"
            />
          )}
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
            Magazine advertising contract
          </h1>
          {contract?.company_name && (
            <p className="mt-2 text-slate-600 dark:text-slate-300">{contract.company_name}</p>
          )}
          {contract?.contract_from_name && (
            <p className="mt-1 text-sm text-slate-500">Contract from: {contract.contract_from_name}</p>
          )}
        </header>

        {loading ? (
          <p className="text-center text-slate-500">Loading contract…</p>
        ) : error && !contract ? (
          <div className="mx-auto max-w-xl rounded-xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
            {error}
          </div>
        ) : contract ? (
          <div className="mx-auto max-w-3xl space-y-6">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={generatingPdf}
                className="rounded-lg border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-slate-800"
              >
                {generatingPdf ? 'Generating PDF…' : 'Download contract PDF'}
              </button>
            </div>
            {contract.proposal?.line_items?.length ? (
              <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
                <h2 className="mb-4 text-lg font-medium text-slate-900 dark:text-slate-100">Order summary</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500">
                        <th className="py-2 pr-4">Publication / Issue</th>
                        <th className="py-2 pr-4">Ad slot</th>
                        <th className="py-2 pr-4">Size</th>
                        <th className="py-2">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contract.proposal.line_items.map((line, index) => (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="py-3 pr-4">
                            {line.publication || '—'}
                            <br />
                            <span className="text-slate-500">{line.issue || '—'}</span>
                          </td>
                          <td className="py-3 pr-4">{line.label}</td>
                          <td className="py-3 pr-4">{line.size || '—'}</td>
                          <td className="py-3">{line.price_formatted}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 text-right text-base font-semibold text-slate-900 dark:text-slate-100">
                  Total: {contract.proposal.total_price_formatted}
                </p>
              </div>
            ) : null}

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800">
              <h2 className="mb-3 text-lg font-medium text-slate-900 dark:text-slate-100">Contract</h2>
              <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-200">
                {contract.body}
              </pre>
            </div>

            {contract.terms_and_conditions && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800">
                <h2 className="mb-3 text-lg font-medium text-slate-900 dark:text-slate-100">
                  Terms &amp; Conditions
                </h2>
                <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-200">
                  {contract.terms_and_conditions}
                </pre>
              </div>
            )}

            {isSigned ? (
              <div className="rounded-xl border border-green-200 bg-green-50 p-6 text-center dark:border-green-800 dark:bg-green-950">
                <p className="text-lg font-semibold text-green-800 dark:text-green-200">Contract signed</p>
                <p className="mt-2 text-sm text-green-700 dark:text-green-300">
                  Signed by {contract.signer_name}
                  {contract.signed_at && (
                    <> on {new Date(contract.signed_at).toLocaleString('en-GB')}</>
                  )}
                </p>
                <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
                  Thank you. Your sales representative will be in touch regarding next steps.
                </p>
                {(contract.purchase_order_number || contract.vat_number) && (
                  <dl className="mt-4 text-sm text-slate-600 dark:text-slate-300">
                    {contract.purchase_order_number && (
                      <div><dt className="inline font-medium">PO number: </dt><dd className="inline">{contract.purchase_order_number}</dd></div>
                    )}
                    {contract.vat_number && (
                      <div><dt className="inline font-medium">VAT number: </dt><dd className="inline">{contract.vat_number}</dd></div>
                    )}
                  </dl>
                )}
              </div>
            ) : (
              <form
                onSubmit={handleSign}
                className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900"
              >
                <h2 className="mb-4 text-lg font-medium text-slate-900 dark:text-slate-100">
                  Approve &amp; sign
                </h2>
                <label className="mb-4 flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    disabled={submitting}
                  />
                  <span>
                    I have read and agree to the terms of this magazine advertising contract on behalf of{' '}
                    {contract.company_name || 'my organisation'}.
                  </span>
                </label>
                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Purchase Order (PO) number <span className="font-normal text-slate-500">(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                    value={purchaseOrderNumber}
                    onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                    placeholder="Enter PO number"
                    disabled={submitting}
                  />
                </div>
                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    VAT number <span className="font-normal text-slate-500">(where applicable)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                    value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                    placeholder="Enter VAT number"
                    disabled={submitting}
                  />
                </div>
                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Full name (electronic signature)
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Type your full name"
                    required
                    disabled={submitting}
                  />
                </div>
                {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting || !agreed || !signerName.trim()}
                  className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Signing…' : 'Approve & sign'}
                </button>
              </form>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PageSignContract;
